import React, { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Stack,
  Snackbar,
  Tooltip,
  Tabs,
  Tab,
  Badge,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  DeleteForever as DeleteForeverIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import BlockedAccountsTab from './BlockedAccountsTab';
import NewcomersTab from './NewcomersTab';

const NEWCOMERS_TAB = 6;

/**
 * Admin Panel for Verifying Organization Memberships
 * 
 * Features:
 * - Displays all pending membership verifications in a table
 * - Quick document preview in tooltip/modal
 * - One-click verification
 * - Document download option
 * 
 * Note for future developers:
 * This panel is designed to work with AI MCP (Model Context Protocol) agents
 * for automated document verification. The document naming pattern
 * ({user_id}_{organization_id}.extension) enables AI agents to easily
 * identify and process verification documents.
 */
const EXCUSES = {
  deactivate: [
    'Your account has been temporarily suspended pending a review of your recent activity.',
    'Your account has been deactivated due to a violation of our community guidelines.',
    'Your account has been temporarily disabled pending identity verification.',
    'Your account has been suspended while we investigate a reported concern.',
  ],
  reactivate: [
    'Your account has been reactivated. Welcome back to Otable.',
    'Your account suspension has been lifted after review. You may now log in.',
  ],
  delete: [
    'Your account has been permanently removed per our terms of service.',
    'Your account and all associated data have been permanently deleted following a review.',
    'Your account has been removed due to repeated policy violations.',
  ],
};

const VerifyMembershipsPanel = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [previewDialog, setPreviewDialog] = useState({ open: false, membership: null });
  const [verifying, setVerifying] = useState(null);
  // ?tab=blocked (from a security alert) opens the Blocked Accounts tab
  const [currentTab, setCurrentTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'blocked' ? 5 : tab === 'newcomers' ? NEWCOMERS_TAB : 0;
  });
  // Newcomers: the count since this admin's last visit, shown on the tab; opening the tab marks it seen
  const { data: newcomers, error: newcomersError } = useSWR('/users/admin-newcomers/', fetcher, { revalidateOnFocus: false });
  const [newcomersSeen, setNewcomersSeen] = useState(false);
  useEffect(() => {
    if (currentTab !== NEWCOMERS_TAB || !newcomers || newcomersSeen) return;
    setNewcomersSeen(true);             // the highlight keeps the previous visit for this page view
    axiosService.post('/users/admin-newcomers/').catch(() => {});
  }, [currentTab, newcomers, newcomersSeen]);

  // User accounts tab state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [actionDialog, setActionDialog] = useState({
    open: false, user: null, action: null, reason: '', notifyEmail: false, processing: false,
  });

  // Fetch all memberships
  const { data: allData, error, isLoading, mutate } = useSWR(
    '/consortium/organization-memberships/',
    fetcher
  );

  const handleVerify = async (membershipId) => {
    try {
      setVerifying(membershipId);
      await axiosService.post(`/consortium/organization-memberships/${membershipId}/verify/`);
      setSnackbar({ open: true, message: 'Membership verified successfully', severity: 'success' });
      mutate();
    } catch (e) {
      console.error('Failed to verify membership:', e);
      const errorMsg = e.response?.data?.error || 'Failed to verify membership';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setVerifying(null);
    }
  };

  const handleRevoke = async (membershipId) => {
    try {
      setVerifying(membershipId);
      await axiosService.post(`/consortium/organization-memberships/${membershipId}/unverify/`);
      setSnackbar({ open: true, message: 'Verification revoked successfully', severity: 'success' });
      mutate();
    } catch (e) {
      console.error('Failed to revoke verification:', e);
      const errorMsg = e.response?.data?.error || 'Failed to revoke verification';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setVerifying(null);
    }
  };

  const handleOpenPreview = (membership) => {
    setPreviewDialog({ open: true, membership });
  };

  const handleClosePreview = () => {
    setPreviewDialog({ open: false, membership: null });
  };

  const handleDownloadDocument = (membership) => {
    if (membership.verification_document) {
      window.open(membership.verification_document, '_blank');
    }
  };

  // ── User accounts tab ──────────────────────────────────────────────────

  const loadUsers = useCallback(async (search = '') => {
    setUsersLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await axiosService.get(`/users/admin-list/${params}`);
      setUsers(res.data.results || []);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load users.', severity: 'error' });
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentTab === 4) loadUsers(userSearch);
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const openActionDialog = (user, action) => {
    setActionDialog({ open: true, user, action, reason: '', notifyEmail: false, processing: false });
  };

  const closeActionDialog = () => {
    setActionDialog(d => ({ ...d, open: false, processing: false }));
  };

  const handleAccountAction = async () => {
    const { user, action, reason, notifyEmail } = actionDialog;
    setActionDialog(d => ({ ...d, processing: true }));
    const urlMap = {
      deactivate: `/users/${user.id}/admin-deactivate/`,
      reactivate: `/users/${user.id}/admin-reactivate/`,
      delete:     `/users/${user.id}/admin-delete/`,
    };
    const labelMap = { deactivate: 'deactivated', reactivate: 'reactivated', delete: 'deleted' };
    try {
      await axiosService.post(urlMap[action], { reason, notify_email: notifyEmail });
      setSnackbar({ open: true, message: `User account ${labelMap[action]}.`, severity: 'success' });
      closeActionDialog();
      loadUsers(userSearch);
    } catch (e) {
      const msg = e.response?.data?.error || `Failed to ${action} account.`;
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setActionDialog(d => ({ ...d, processing: false }));
    }
  };

  const getFileExtension = (url) => {
    if (!url) return '';
    return url.split('.').pop().toLowerCase();
  };

  const isImageFile = (url) => {
    const ext = getFileExtension(url);
    return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
  };

  const isPDFFile = (url) => {
    return getFileExtension(url) === 'pdf';
  };

  if (isLoading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load memberships. {error.response?.data?.error || error.message}
        </Alert>
      </Container>
    );
  }

  const allMemberships = allData?.results || [];
  
  // Categorize memberships
  const pendingRequests = allMemberships.filter(m => 
    !m.is_verified && m.verification_document
  );
  const unverified = allMemberships.filter(m => 
    !m.is_verified && !m.verification_document
  );
  const verified = allMemberships.filter(m => 
    m.is_verified && m.verified_at
  );
  
  // For now, we'll treat unverified previously verified memberships as "revoked"
  const revoked = allMemberships.filter(m => 
    !m.is_verified && m.verified_by && !m.verification_document
  );

  const tabs = [
    { label: 'Pending Requests', count: pendingRequests.length, data: pendingRequests },
    { label: 'Unverified', count: unverified.length, data: unverified },
    { label: 'Verified', count: verified.length, data: verified },
    { label: 'Revoked', count: revoked.length, data: revoked },
    { label: 'User Accounts', count: null, data: [] },
    { label: 'Blocked Accounts', count: null, data: [] },
    { label: 'Newcomers', count: null, data: [], chip: newcomersSeen ? 0 : (newcomers?.since_last_visit || 0) },
  ];

  const currentMemberships = currentTab < 4 ? tabs[currentTab].data : [];

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      
        <Box display="flex" alignItems="center" mb={3}>
         <Box>
          <Typography variant="h4" fontWeight={800} sx={{ flex: 1, letterSpacing: 0.2 }}><BackButton position="inline" /> Verify Memberships</Typography>
          <Typography variant="body1" color="text.secondary" ml={4}>
            Review and verify organization membership documents
          </Typography>
          </Box>
        </Box>

      <Paper sx={{ p: 3 }}>


        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.chip !== undefined ? (
                  <Box display="flex" alignItems="center" gap={0.75} sx={{ px: 1 }}>
                    {tab.label}
                    {tab.chip > 0 && (
                      <Chip size="small" color="primary" label={`+${tab.chip.toLocaleString()}`}
                        title={`${tab.chip} joined since your last visit`} sx={{ height: 20, fontSize: 12, fontWeight: 700 }} />
                    )}
                  </Box>
                ) : (
                  <Badge badgeContent={tab.count} color="primary" max={999}>
                    <Box sx={{ px: 1 }}>{tab.label}</Box>
                  </Badge>
                )}
              />
            ))}
          </Tabs>
        </Box>

        {currentTab === 5 && <BlockedAccountsTab />}
        {currentTab === NEWCOMERS_TAB && (
          <NewcomersTab data={newcomers} error={newcomersError} lastVisit={newcomers?.last_visit || null} />
        )}

        {/* ── User Accounts tab ───────────────────────────────────── */}
        {currentTab === 4 && (
          <Box>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <TextField
                size="small"
                placeholder="Search by name, email or username…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadUsers(userSearch)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ width: 320 }}
              />
              <Button variant="outlined" size="small" onClick={() => loadUsers(userSearch)}>
                Search
              </Button>
            </Stack>

            {usersLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Alert severity="info">No users found.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: u.is_active ? 'primary.main' : 'text.disabled' }}>
                              {(u.name || u.username).charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{u.name || u.username}</Typography>
                              {u.is_staff && <Chip label="Staff" size="small" color="warning" sx={{ fontSize: 10, height: 16 }} />}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.is_active ? 'Active' : 'Inactive'}
                            color={u.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {u.date_joined ? new Date(u.date_joined).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {u.is_active ? (
                              <Tooltip title="Deactivate account">
                                <Button size="small" variant="outlined" color="warning"
                                  startIcon={<PersonOffIcon />}
                                  onClick={() => openActionDialog(u, 'deactivate')}>
                                  Deactivate
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Reactivate account">
                                <Button size="small" variant="outlined" color="success"
                                  startIcon={<PersonAddIcon />}
                                  onClick={() => openActionDialog(u, 'reactivate')}>
                                  Reactivate
                                </Button>
                              </Tooltip>
                            )}
                            <Tooltip title="Permanently delete account">
                              <Button size="small" variant="outlined" color="error"
                                startIcon={<DeleteForeverIcon />}
                                onClick={() => openActionDialog(u, 'delete')}>
                                Delete
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {currentTab < 4 && currentMemberships.length === 0 && (
          <Alert severity="info">
            {currentTab === 0 && 'No pending verification requests at this time.'}
            {currentTab === 1 && 'No unverified memberships without documents.'}
            {currentTab === 2 && 'No verified memberships yet.'}
            {currentTab === 3 && 'No revoked verifications.'}
          </Alert>
        )}
        {currentTab < 4 && currentMemberships.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>User</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentMemberships.map((membership) => (
                  <TableRow
                    key={membership.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {membership.user_name?.charAt(0) || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {membership.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {membership.user_email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {membership.org_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {membership.org_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={membership.role} size="small" />
                    </TableCell>
                    <TableCell>
                      {membership.department_name || <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      {membership.verification_document ? (
                        <Tooltip title="Click to preview document">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenPreview(membership)}
                            color="primary"
                          >
                            <DocumentIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No document
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {membership.verified_at 
                          ? new Date(membership.verified_at).toLocaleDateString()
                          : new Date(membership.created).toLocaleDateString()
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {membership.verification_document && (
                          <>
                            <Tooltip title="Preview Document">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPreview(membership)}
                                color="info"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(membership)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        
                        {/* Pending Requests tab - show Verify button */}
                        {currentTab === 0 && (
                          <Tooltip title="Verify Membership">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={
                                  verifying === membership.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <CheckCircleIcon />
                                  )
                                }
                                onClick={() => handleVerify(membership.id)}
                                disabled={verifying === membership.id}
                              >
                                Verify
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        
                        {/* Verified tab - show Revoke button */}
                        {currentTab === 2 && (
                          <Tooltip title="Revoke Verification">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={
                                  verifying === membership.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <CancelIcon />
                                  )
                                }
                                onClick={() => handleRevoke(membership.id)}
                                disabled={verifying === membership.id}
                              >
                                Revoke
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Document Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Verification Document</Typography>
              {previewDialog.membership && (
                <Typography variant="body2" color="text.secondary">
                  {previewDialog.membership.user_name} - {previewDialog.membership.org_name}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewDialog.membership?.verification_document ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {isImageFile(previewDialog.membership.verification_document) ? (
                <Box
                  component="img"
                  src={previewDialog.membership.verification_document}
                  alt="Verification Document"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    margin: 'auto'
                  }}
                />
              ) : isPDFFile(previewDialog.membership.verification_document) ? (
                <iframe
                  src={previewDialog.membership.verification_document}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="PDF Preview"
                />
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Preview not available for this file type. Please download the document to view it.
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadDocument(previewDialog.membership)}
                    sx={{ mt: 2 }}
                  >
                    Download Document
                  </Button>
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="warning">No document available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} startIcon={<CancelIcon />}>
            Close
          </Button>
          {currentTab === 0 && previewDialog.membership && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => {
                if (previewDialog.membership) {
                  handleVerify(previewDialog.membership.id);
                  handleClosePreview();
                }
              }}
              disabled={verifying !== null}
            >
              Verify Membership
            </Button>
          )}
          {currentTab === 2 && previewDialog.membership && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => {
                if (previewDialog.membership) {
                  handleRevoke(previewDialog.membership.id);
                  handleClosePreview();
                }
              }}
              disabled={verifying !== null}
            >
              Revoke Verification
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Account Action Dialog */}
      <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            {actionDialog.action === 'deactivate' && <PersonOffIcon color="warning" />}
            {actionDialog.action === 'reactivate' && <PersonAddIcon color="success" />}
            {actionDialog.action === 'delete' && <DeleteForeverIcon color="error" />}
            <Box>
              <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                {actionDialog.action} Account
              </Typography>
              {actionDialog.user && (
                <Typography variant="body2" color="text.secondary">
                  {actionDialog.user.name} · {actionDialog.user.email}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Select a reason or write a custom one:
          </Typography>

          {/* Preset excuses */}
          <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
            {(EXCUSES[actionDialog.action] || []).map((text, i) => (
              <Chip
                key={i}
                label={text}
                size="small"
                variant={actionDialog.reason === text ? 'filled' : 'outlined'}
                color={actionDialog.reason === text ? 'primary' : 'default'}
                onClick={() => setActionDialog(d => ({ ...d, reason: text }))}
                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
              />
            ))}
          </Stack>

          {/* Custom reason */}
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Reason"
            placeholder="Custom reason…"
            value={actionDialog.reason}
            onChange={e => setActionDialog(d => ({ ...d, reason: e.target.value }))}
            sx={{ mb: 2 }}
          />

          {/* Email notification toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={actionDialog.notifyEmail}
                onChange={e => setActionDialog(d => ({ ...d, notifyEmail: e.target.checked }))}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Notify user via email
                {actionDialog.notifyEmail && !actionDialog.reason && (
                  <Typography component="span" variant="caption" color="warning.main" ml={1}>
                    (add a reason to include it in the email)
                  </Typography>
                )}
              </Typography>
            }
          />

          {actionDialog.action === 'delete' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This action is permanent and cannot be undone.
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeActionDialog} disabled={actionDialog.processing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.action === 'reactivate' ? 'success' : actionDialog.action === 'deactivate' ? 'warning' : 'error'}
            onClick={handleAccountAction}
            disabled={actionDialog.processing}
            startIcon={actionDialog.processing ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'capitalize' }}
          >
            {actionDialog.processing ? 'Processing…' : actionDialog.action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VerifyMembershipsPanel;
